(function () {
  const KLAVIYO_EVENT = 'Notify Me Requested';

  function getKlaviyoQueue() {
    window._learnq = window._learnq || [];
    return window._learnq;
  }

  function pushKlaviyoEvent(eventName, payload) {
    const klaviyo = getKlaviyoQueue();
    klaviyo.push(['identify', { $email: payload.email }]);
    klaviyo.push(['track', eventName, payload]);
  }

  async function subscribeToList(publicKey, listId, email, payload) {
    const body = {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: 'Back in Stock Waitlist',
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email,
                properties: {
                  last_bis_product_id: payload.product_id,
                  last_bis_product_title: payload.product_title,
                  last_bis_product_url: payload.product_url,
                  last_bis_product_image: payload.product_image,
                  last_bis_variant_id: payload.variant_id,
                  last_bis_variant_title: payload.variant_title,
                  last_bis_country: payload.country,
                },
              },
            },
          },
        },
        relationships: {
          list: { data: { type: 'list', id: listId } },
        },
      },
    };

    const res = await fetch(
      `https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(publicKey)}`,
      {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          revision: '2024-10-15',
        },
        body: JSON.stringify(body),
      }
    );

    if (!res.ok) {
      const text = await res.text().catch(() => '');
      console.warn('[notify-me] list subscribe failed', res.status, text);
    }
  }

  function trapFocus(modal) {
    const focusable = modal.querySelectorAll(
      'input, button, [tabindex]:not([tabindex="-1"])'
    );
    if (focusable.length === 0) return null;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    function handler(e) {
      if (e.key !== 'Tab') return;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    modal.addEventListener('keydown', handler);
    return handler;
  }

  class NotifyMeController {
    constructor(modal) {
      this.modal = modal;
      this.form = modal.querySelector('[data-notify-me-form]');
      this.body = modal.querySelector('[data-notify-me-body]');
      this.success = modal.querySelector('[data-notify-me-success]');
      this.successEmail = modal.querySelector('[data-notify-me-success-email]');
      this.submitButton = modal.querySelector('[data-notify-me-submit]');
      this.submitLabel = modal.querySelector('[data-notify-me-submit-label]');
      this.spinner = modal.querySelector('[data-notify-me-spinner]');
      this.errorEl = modal.querySelector('[data-notify-me-error]');
      this.lastFocus = null;
      this.focusHandler = null;

      this.modal.addEventListener('click', (e) => {
        if (e.target === this.modal) this.close();
        if (e.target.closest('[data-notify-me-close]')) this.close();
      });

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.modal.classList.contains('is-visible')) {
          this.close();
        }
      });

      this.form.addEventListener('submit', (e) => this.handleSubmit(e));
    }

    open(context) {
      if (context) this.updateContext(context);
      this.resetView();
      this.lastFocus = document.activeElement;
      this.modal.hidden = false;
      requestAnimationFrame(() => {
        this.modal.classList.add('is-visible');
      });
      document.body.style.overflow = 'hidden';
      this.focusHandler = trapFocus(this.modal);
      const emailInput = this.form.querySelector('input[type="email"]');
      if (emailInput) emailInput.focus();
    }

    close() {
      this.modal.classList.remove('is-visible');
      document.body.style.overflow = '';
      setTimeout(() => {
        this.modal.hidden = true;
      }, 250);
      if (this.lastFocus && typeof this.lastFocus.focus === 'function') {
        this.lastFocus.focus();
      }
    }

    resetView() {
      this.body.hidden = false;
      this.success.hidden = true;
      this.errorEl.hidden = true;
      this.setLoading(false);
      const emailInput = this.form.querySelector('input[type="email"]');
      if (emailInput) emailInput.value = '';
    }

    updateContext(context) {
      const variantInput = this.form.querySelector('[data-notify-me-variant]');
      const variantTitleInput = this.form.querySelector('[data-notify-me-variant-title]');
      const priceInput = this.form.querySelector('[data-notify-me-price]');
      const reasonInput = this.form.querySelector('input[name="reason"]');
      if (variantInput && context.variantId) variantInput.value = context.variantId;
      if (variantTitleInput && context.variantTitle) variantTitleInput.value = context.variantTitle;
      if (priceInput && context.price) priceInput.value = context.price;
      if (reasonInput && context.reason) reasonInput.value = context.reason;
    }

    setLoading(isLoading) {
      this.submitButton.disabled = isLoading;
      if (this.spinner) this.spinner.classList.toggle('hidden', !isLoading);
      if (this.submitLabel) this.submitLabel.classList.toggle('hidden', isLoading);
    }

    async handleSubmit(event) {
      event.preventDefault();
      this.errorEl.hidden = true;

      const formData = new FormData(this.form);
      const email = (formData.get('email') || '').toString().trim();
      if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        this.errorEl.hidden = false;
        this.errorEl.textContent = 'Please enter a valid email address.';
        return;
      }

      this.setLoading(true);

      const payload = {
        email,
        product_id: formData.get('product_id'),
        product_title: formData.get('product_title'),
        product_url: formData.get('product_url'),
        product_image: formData.get('product_image'),
        variant_id: formData.get('variant_id'),
        variant_title: formData.get('variant_title'),
        price: formData.get('price'),
        country: formData.get('country'),
        country_name: formData.get('country_name'),
        reason: formData.get('reason'),
      };

      try {
        pushKlaviyoEvent(KLAVIYO_EVENT, payload);

        const publicKey = this.form.dataset.klaviyoPublicKey;
        const listId = this.form.dataset.klaviyoListId;
        if (publicKey && listId && payload.reason === 'warehouse') {
          await subscribeToList(publicKey, listId, email, payload);
        }

        this.showSuccess(email);
      } catch (err) {
        console.error('[notify-me] submission failed', err);
        this.setLoading(false);
        this.errorEl.hidden = false;
      }
    }

    showSuccess(email) {
      this.setLoading(false);
      this.body.hidden = true;
      this.success.hidden = false;
      if (this.successEmail) {
        this.successEmail.textContent = email;
      }
    }
  }

  function init() {
    const modals = document.querySelectorAll('[data-notify-me-modal]');
    modals.forEach((modal) => {
      if (modal.dataset.notifyMeReady === 'true') return;
      modal.dataset.notifyMeReady = 'true';
      const controller = new NotifyMeController(modal);
      modal.notifyMe = controller;
    });

    document.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-notify-me-trigger]');
      if (!trigger) return;
      e.preventDefault();
      const targetId = trigger.getAttribute('data-notify-me-target');
      const modal = targetId ? document.getElementById(targetId) : document.querySelector('[data-notify-me-modal]');
      if (!modal || !modal.notifyMe) return;
      modal.notifyMe.open({
        variantId: trigger.getAttribute('data-variant-id'),
        variantTitle: trigger.getAttribute('data-variant-title'),
        price: trigger.getAttribute('data-price'),
        reason: trigger.getAttribute('data-reason'),
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
